// Saudi delivery addresses — one definition for the checkout page, saved
// addresses and the market-checkout function (supabase/functions/market-
// checkout/saAddress.js is a byte-identical copy; a test fails on drift).
// Pure: no imports, no platform APIs.
//
// WHAT THE SHIPPING ROUTE REQUIRES (HioBuy SA001G, read 2026-10-19):
//   • "As required by the Saudi government, logistics service providers must
//     use national addresses for delivery starting from January 1, 2026 …
//     the Short Address (4 letters + 4 digits). Failure to fill in the Short
//     Address will affect customs clearance and delivery, and may result in
//     fines … borne by the customer." → the Short Address is MANDATORY here.
//   • The shipment receiver needs name, mobile, country and one address line;
//     province, city, district, postal code and a second line are optional.
//     Postal code, declared value, customs codes and recipient identity are
//     OPTIONAL on the route — so no ID number is collected.
//
// FORMAT IS NOT VERIFICATION: a code that matches 4 letters + 4 digits may
// still not exist. Only an authorised National Address service (SPL) can
// confirm one; nothing here says "verified".

const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩', FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
/** Arabic-Indic / Persian digits → 0-9; everything else unchanged. */
export const toLatinDigits = (v) => String(v ?? '').replace(/[٠-٩۰-۹]/g, (d) => String(AR_DIGITS.includes(d) ? AR_DIGITS.indexOf(d) : FA_DIGITS.indexOf(d)));

/** Saudi mobile in national form 5XXXXXXXX, from +966 / 00966 / 966 / 0 prefixes, spaces, dashes. */
export function normalizeSaMobile(v) {
  const d = toLatinDigits(v).replace(/[\s\-()]/g, '').replace(/^(\+966|00966|966)/, '').replace(/^0/, '');
  return /^5\d{8}$/.test(d) ? d : null;
}

/** National Address short form: 4 letters + 4 digits (RAGI2929), normalized to upper case. */
export function normalizeShortAddress(v) {
  const s = toLatinDigits(v).replace(/[\s-]/g, '').toUpperCase();
  return /^[A-Z]{4}\d{4}$/.test(s) ? s : null;
}

// Saudi Arabia's extent (with margin): a pin outside it is a mistake.
export const SA_BOUNDS = { south: 16, north: 32.5, west: 34.4, east: 55.8 };
export const inSaudiArabia = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng)
  && lat >= SA_BOUNDS.south && lat <= SA_BOUNDS.north && lng >= SA_BOUNDS.west && lng <= SA_BOUNDS.east;

// Major cities, for suggestions and for the carrier: a city typed in Arabic is
// sent to the shipment in English when it is one of these (others as typed).
export const SA_CITIES = [
  ['الرياض', 'Riyadh'], ['جدة', 'Jeddah'], ['مكة المكرمة', 'Makkah'], ['المدينة المنورة', 'Madinah'], ['الدمام', 'Dammam'],
  ['الخبر', 'Khobar'], ['الظهران', 'Dhahran'], ['الأحساء', 'Al Ahsa'], ['الجبيل', 'Jubail'], ['القطيف', 'Qatif'],
  ['بريدة', 'Buraydah'], ['عنيزة', 'Unaizah'], ['الطائف', 'Taif'], ['تبوك', 'Tabuk'], ['حائل', 'Hail'],
  ['أبها', 'Abha'], ['خميس مشيط', 'Khamis Mushait'], ['جازان', 'Jazan'], ['نجران', 'Najran'], ['ينبع', 'Yanbu'],
].map(([ar, en]) => ({ ar, en }));
export const cityInEnglish = (c) => {
  const t = String(c ?? '').trim().toLowerCase();
  return SA_CITIES.find((x) => x.ar === String(c ?? '').trim() || x.en.toLowerCase() === t)?.en ?? String(c ?? '').trim();
};

const str = (v, max) => String(v ?? '').trim().replace(/\s+/g, ' ').slice(0, max);
const num = (v) => (v === '' || v == null ? null : Number(v));

/**
 * { field: errorCode } — empty when the address can be used. Keys (camelCase):
 * name, phone, shortAddress, city, district, street, buildingNo (required);
 * postalCode, additionalNo, unit, details, instructions, lat, lng, label (optional).
 */
export function validateAddress(a = {}) {
  const e = {};
  if (str(a.name, 120).length < 3) e.name = 'required';
  if (!normalizeSaMobile(a.phone)) e.phone = a.phone ? 'format' : 'required';
  if (!normalizeShortAddress(a.shortAddress)) e.shortAddress = a.shortAddress ? 'format' : 'required';
  if (!str(a.city, 80)) e.city = 'required';
  if (!str(a.district, 120)) e.district = 'required';
  if (str(a.street, 160).length < 2) e.street = 'required';
  const b = toLatinDigits(a.buildingNo).trim();
  if (!b) e.buildingNo = 'required'; else if (!/^\d{4}$/.test(b)) e.buildingNo = 'format';
  const p = toLatinDigits(a.postalCode).trim();
  if (p && !/^\d{5}$/.test(p)) e.postalCode = 'format';
  const x = toLatinDigits(a.additionalNo).trim();
  if (x && !/^\d{4}$/.test(x)) e.additionalNo = 'format';
  const lat = num(a.lat), lng = num(a.lng);
  if ((lat != null || lng != null) && !inSaudiArabia(lat, lng)) e.location = 'outside';
  return e;
}

/**
 * The stored delivery snapshot (quote and order). Keeps the keys earlier code
 * reads — name, phone, city, district, address, shortAddress, notes — and adds
 * the structured ones beside them. Call only after validateAddress is empty.
 */
export function toDelivery(a) {
  const lat = num(a.lat), lng = num(a.lng);
  const d = {
    name: str(a.name, 120),
    phone: `+966${normalizeSaMobile(a.phone)}`,
    shortAddress: normalizeShortAddress(a.shortAddress),
    city: str(a.city, 80),
    district: str(a.district, 120),
    street: str(a.street, 160),
    buildingNo: toLatinDigits(a.buildingNo).trim(),
    postalCode: toLatinDigits(a.postalCode).trim() || null,
    additionalNo: toLatinDigits(a.additionalNo).trim() || null,
    unit: str(a.unit, 40) || null,
    details: str(a.details, 300) || null,
    instructions: str(a.instructions, 500) || null,
    lat: inSaudiArabia(lat, lng) ? Math.round(lat * 1e6) / 1e6 : null,
    lng: inSaudiArabia(lat, lng) ? Math.round(lng * 1e6) / 1e6 : null,
  };
  d.address = [`${d.buildingNo} ${d.street}`, d.unit && `#${d.unit}`, d.details].filter(Boolean).join(', ');
  d.notes = d.instructions;
  return d;
}

/**
 * The receiver of a HioBuy international shipment (POST /v1/fulfillment/
 * shipments/create). The schema has no short-address field, so it leads
 * address_line1, where a Saudi carrier reads the first line. UNRESOLVED with
 * HioBuy: whether their last-mile partner reads it from there or wants it
 * elsewhere — to be confirmed before the first live shipment.
 */
export function toHioBuyReceiver(d) {
  return {
    name: d.name,
    mobile: d.phone,
    country_code: 'SA',
    city: cityInEnglish(d.city),
    district: d.district,
    postal_code: d.postalCode || undefined,
    address_line1: `${d.shortAddress} ${d.buildingNo} ${d.street}`.slice(0, 200),
    address_line2: [d.district, d.unit && `Unit ${d.unit}`, d.additionalNo && `Additional No. ${d.additionalNo}`, d.details].filter(Boolean).join(', ').slice(0, 200) || undefined,
  };
}
